import { FORM_ERROR } from 'final-form';
import { useNavigate } from 'react-router-dom';
import { datadogLogs } from '@datadog/browser-logs';
import { showToast } from 'components/Toast';
import { getApiDebuggingId, getApiErrorStatus, getApiHttpStatus } from 'helpers/rtk-utils';
import {
  useCreateClientFilterMutation,
  useUpdateClientFilterMutation,
} from 'services/clientFiltersApi';
import { AdminFilter } from 'store/clientFilters/types';
import { FILTER_SAVED } from '../constants';
import { FilterFormValues } from './types';
import { buildCreatePayload, buildUpdatePayload, describeFilterSaveFailure } from './utils';
import { trackEvent } from 'helpers/mixpanelEvents';

export const useFilterSubmit = (clientId: number, filter: AdminFilter | undefined) => {
  const navigate = useNavigate();
  const [createFilter] = useCreateClientFilterMutation();
  const [updateFilter] = useUpdateClientFilterMutation();

  const saveFilter = async (values: FilterFormValues) => {
    if (!filter) {
      await createFilter({ clientId, body: buildCreatePayload(values) }).unwrap();
      return true;
    }
    const body = buildUpdatePayload(filter, values);
    if (Object.keys(body).length === 0) return false;
    await updateFilter({ clientId, filterId: filter.filter_id, body }).unwrap();
    return true;
  };

  return async (values: FilterFormValues) => {
    try {
      const hasSaved = await saveFilter(values);
      if (hasSaved) {
        trackEvent('Filter Saved', {
          screen: 'filter_detail',
          interaction_type: filter ? 'edit' : 'create',
        });
        showToast(FILTER_SAVED, 'success');
      }
      navigate('/filters');
    } catch (error: unknown) {
      const { message, isNameTaken, isExpected } = describeFilterSaveFailure(error);
      const logContext = {
        client_id: clientId,
        filter_id: filter?.filter_id,
        status: getApiErrorStatus(error),
        debugging_id: getApiDebuggingId(error),
      };
      if (isExpected) {
        datadogLogs.logger.info('Client filter save rejected', logContext);
      } else {
        datadogLogs.logger.error(
          'Failed to save client filter',
          logContext,
          error instanceof Error ? error : undefined
        );
      }
      showToast(message, 'error', getApiHttpStatus(error));
      return isNameTaken ? { filter_name: message } : { [FORM_ERROR]: message };
    }
  };
};
